
import { Box, Button, TextField, Typography, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { Close, Done, Visibility, VisibilityOff } from '@mui/icons-material';
import { router } from '@inertiajs/react';
import { useEffect, useState } from 'react';
import theme from './Theme';
import AuthLayout from './AuthLayout';
import { InfoOutlined } from '@mui/icons-material';

export default function SetPassword({ email: initialEmail = '', password: initialPassword = '' }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState(initialPassword);
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [error, setError] = useState('');
  const [allRequirementsPassed, setAllRequirementsPassed] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    minLength: false,
    oneLowercase: false,
    oneUppercase: false,
    oneNumber: false,
    oneSpecialCharacter: false,
    match: false
  });

  const handleLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const key = new URLSearchParams(window.location.search).get('key');
    fetch(window.location.href, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ key, password, 'password-confirm': passwordConfirmation })
    }).then((response) => {
      if (response.ok) {
        router.visit('/');
      } else {
        response.json().then((data) => {
          setError(data.error);
        });
      }
    }).catch((error) => {
      setError('Login failed: ' + error);
    });
  };

  useEffect(() => {
    const passwordRequirementsStatus = {
      minLength: password.length >= 8,
      oneLowercase: /[a-z]/.test(password),
      oneUppercase: /[A-Z]/.test(password),
      oneNumber: /\d/.test(password),
      oneSpecialCharacter: /[!@#$%^&*()_+{}\[\]:;<>,.?~\\/-]/.test(password),
      match: password === passwordConfirmation
    }
    setPasswordRequirements(passwordRequirementsStatus);
    setAllRequirementsPassed(Object.values(passwordRequirementsStatus).every(Boolean));
  }, [password, passwordConfirmation]);

  const passwordRequirement = (requirementPassed: boolean, requirement: string) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {requirementPassed ? <Done sx={{ color: '#4CAF50' }} /> : <Close />}
      <Typography variant="body2">{requirement}</Typography>
    </Box>
  )
  return (
    <AuthLayout title="PrecisionRX | Set Password">
      <Typography variant="body1" sx={{ color: 'white', mt: {mobile: '0px', tablet_wide: '24px'} }}>
        Create a strong password.
      </Typography>
      <Box component="form" sx={{ width: '100%' }}>
        {email && <TextField
          margin="normal"
          disabled
          required
          fullWidth
          id="email"
          placeholder="Email address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          onChange={(e) => setEmail(e.target.value)}
        />}

        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          type={showPassword ? 'text' : 'password'}
          placeholder="Password"
          id="password"
          autoComplete="current-password"
          value={password}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          onChange={(e) => setPassword(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password-confirmation"
          type={showPasswordConfirmation ? 'text' : 'password'}
          id="password-confirmation"
          autoComplete="current-password"
          placeholder="Password confirmation"
          value={passwordConfirmation}
          sx={{
            '& .MuiInputBase-root': { backgroundColor: 'white' },
          }}
          onChange={(e) => setPasswordConfirmation(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                    edge="end"
                  >
                    {showPasswordConfirmation ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }
          }}
        />
        {error && <Typography variant="body2" color="error">{error}</Typography>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-starts', mt: 1, mb: 2 }}>
            <Box sx={{ alignItems: 'center', gap: 1, display: allRequirementsPassed ? 'none' : 'flex' }}>
              
              <Tooltip arrow placement="bottom" title={<>
                  {passwordRequirement(passwordRequirements.minLength, 'Minimum 8 characters')}
                  {passwordRequirement(passwordRequirements.oneLowercase, 'At least one lowercase letter')}
                  {passwordRequirement(passwordRequirements.oneUppercase, 'At least one uppercase letter')}
                  {passwordRequirement(passwordRequirements.oneNumber, 'At least one number')}
                  {passwordRequirement(passwordRequirements.oneSpecialCharacter, 'At least one special character')}
                  {passwordRequirement(passwordRequirements.match, 'Passwords match')}
                </>}>
                <IconButton sx={{ p: 0, display: allRequirementsPassed ? 'none' : 'flex' }}>
                  <InfoOutlined sx={{ color: '#EFC0C0' }} />
                </IconButton>
              </Tooltip>
              <Typography color="#EFC0C0" variant="body2">Password requirements</Typography>

            </Box>
            {allRequirementsPassed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Done sx={{ color: '#4CAF50' }} />
                <Typography color="white" variant="body2">All password requirements met</Typography>
              </Box>
            )}
        </Box>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          color="primary"
          sx={{ mt: 2, '&:disabled': { backgroundColor: theme.pandia.indigo_light }}}
          disabled={!allRequirementsPassed}
          onClick={handleLogin}
        >
          Set Password and Log In
        </Button>
      </Box>
    </AuthLayout>

  );
}
